import { create } from 'zustand'
import zhCN from 'antd/locale/zh_CN'
import enUS from 'antd/locale/en_US'
import th from 'antd/locale/th_TH'
import msMY from 'antd/locale/ms_MY'
import { I18nKeyMap } from '@/types/i18n'
import { getInitData } from './locales'
import { getLanguageEnv } from '@/servers/config'
import storage, { storagekeys } from '@/utils/storage'
import { i18n } from '@yt/fe-shared'

import 'dayjs/locale/zh-cn'
import 'dayjs/locale/en'
import 'dayjs/locale/th'
import 'dayjs/locale/ms'
import dayjs from 'dayjs'

const switchDayjsLocale = (lang: LanguagesType) => {
  const map = {
    'zh-CN': 'zh-cn',
    'en-US': 'en',
    'th-TH': 'th',
    'ms-MY': 'ms'
  }
  dayjs.locale(map[lang] || 'en')
}

type State = {
  currentLang: string | null
  antdLocale: any
  i18nLocale: I18nKeyMap
  allI18nLocale: Record<
    string,
    {
      update_time: number
      data: I18nKeyMap
    }
  >
  allAntdLocale: any
  loading: boolean
}

type Actions = {
  setData: (data: Partial<State>) => void
  switcherLanguage: (lang: LanguagesType) => Promise<any>
}

export type LanguagesType = 'zh-CN' | 'en-US' | 'th-TH' | 'ms-MY'

// 语言数据来源类型
export type LanguageSource = 'storage' | 'url' | 'domain' | 'browser' | 'default'

const defaultState: State = {
  currentLang: null,
  antdLocale: zhCN,
  i18nLocale: {} as I18nKeyMap,
  allI18nLocale: getInitData(),
  // allI18nLocale: {},
  allAntdLocale: {
    ['zh-CN']: zhCN,
    ['en-US']: enUS,
    ['th-TH']: th,
    ['ms-MY']: msMY // 马来西亚语使用马来语 Antd 语言包
  },
  loading: false
}

export const useI18nStore = create<State & Actions>((set, get) => ({
  ...defaultState,
  setData(data: Partial<State>) {
    const obj = Object.assign({}, get(), data)
    set(obj)
  },
  async switcherLanguage(lang: LanguagesType) {
    // 开发模式：使用 URL 参数方式切换语言
    if (import.meta.env.VITE_NODE_ENV === 'dev') {
      // 语种到 locale 参数的映射
      const langToLocale: Record<LanguagesType, string> = {
        'zh-CN': 'zh',
        'en-US': 'en',
        'th-TH': 'th',
        'ms-MY': 'ms'
      }

      const locale = langToLocale[lang]
      const currentUrl = new URL(window.location.href)

      // 移除可能存在的旧 locale 参数
      currentUrl.searchParams.delete('locale')

      // 添加新的 locale 参数
      currentUrl.searchParams.set('locale', locale)
      i18n.changeLocale(locale as LanguagesType)
      // 在当前窗口跳转（或新标签页，根据需求调整）
      window.location.href = currentUrl.toString()
      return
    }

    // 生产模式：使用子域名方式切换语言
    // 根据目标语言获取对应的子域名前缀
    const langToSubdomain: Record<LanguagesType, string> = {
      'zh-CN': '', // 中文不需要子域名前缀
      'en-US': 'en',
      'th-TH': 'th',
      'ms-MY': 'my'
    }

    const targetSubdomain = langToSubdomain[lang]
    const currentHostname = window.location.hostname
    const hostnameParts = currentHostname.split('.')

    // 提取基础域名（去掉语种前缀）
    // 当前可能是 en-test-ad.aiadfly.com 或 test-ad.aiadfly.com 格式
    const firstPart = hostnameParts[0]
    const knownPrefixes = ['en', 'th', 'my']

    let baseName = firstPart
    if (knownPrefixes.some(prefix => firstPart.startsWith(`${prefix}-`))) {
      // 如果有语种前缀（如 en-test-ad），提取基础名称（test-ad）
      baseName = firstPart.substring(firstPart.indexOf('-') + 1)
    }

    // 保留原始域名后缀（aiadfly.com）
    const domainSuffix = hostnameParts.slice(1).join('.')

    // 构建目标URL
    let targetHostname: string
    if (lang === 'zh-CN') {
      // 中文：直接使用基础域名 + 后缀
      targetHostname = `${baseName}.${domainSuffix}`
    } else {
      // 非中文：添加语种子域名前缀 + 连字符 + 基础域名 + 后缀
      targetHostname = `${targetSubdomain}-${baseName}.${domainSuffix}`
    }

    const targetUrl = `${window.location.protocol}//${targetHostname}${window.location.pathname}${window.location.search}`

    // 在新标签页打开目标语言URL
    window.open(targetUrl, '_blank')
  }
}))

export const defaultLang = 'en-US'

export const getLocale = (): [string, LanguageSource] => {
  // Use domain-based language detection
  const lang = getLanguageEnv()
  return [lang, 'domain']
}

export const setLocale = () => {
  // No longer storing language preference in localStorage
  // Language is determined by domain only
  return true
}

export const initLocaleData = () => {
  // console.info('initLocaleData')

  // Get language with query parameter priority in test/dev environment
  const lang = getLanguageEnv()

  // Save current language to storage
  storage.set(storagekeys.currentLang, lang)

  switchDayjsLocale(lang)

  const { allAntdLocale, allI18nLocale, setData } = useI18nStore.getState()

  if (allI18nLocale[lang]) {
    setData({
      currentLang: lang,
      i18nLocale: allI18nLocale[lang].data,
      antdLocale: allAntdLocale[lang]
    })
  }

  // In test/dev environment, add listener for query parameter changes
  // if (import.meta.env.MODE === 'test' || import.meta.env.MODE === 'dev') {
  //   const handlePopState = () => {
  //     const currentLang = getLanguageEnv()
  //     if (currentLang !== useI18nStore.getState().currentLang) {
  //       console.info('Language changed via query parameter, reinitializing...')
  //       initLocaleData()
  //     }
  //   }

  //   // Listen to popstate event (back/forward navigation)
  //   window.addEventListener('popstate', handlePopState)

  //   // Also listen to pushstate/replacestate via monkey-patching
  //   const originalPushState = history.pushState
  //   const originalReplaceState = history.replaceState

  //   history.pushState = function (...args) {
  //     originalPushState.apply(history, args)
  //     handlePopState()
  //   }

  //   history.replaceState = function (...args) {
  //     originalReplaceState.apply(history, args)
  //     handlePopState()
  //   }
  // }
}

export const useSwitcherLanguage = () => {
  const { currentLang, switcherLanguage } = useI18nStore()
  return {
    currentLang,
    switcherLanguage
  }
}
